import { captureLogger as logger } from '../../configure/logger-facade';

const queryMicPermissionStatus = async (): Promise<PermissionStatus | null> => {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return null;
  }
  try {
    return await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
  } catch {
    logger.debug({ event_type: 'MIC_PERMISSION_QUERY_FAILED' });
    return null;
  }
};

/**
 * Read the browser's microphone permission via the Permissions API without
 * prompting. Returns `null` when the API is unavailable or the query throws
 * (some Safari/Firefox versions don't support `microphone` as a name).
 *
 * Unlike `getMicPermission`, it never falls back to `getUserMedia`, so it is
 * safe as a pre-flight check that must not trigger the OS-level prompt.
 */
export async function queryMicPermissionState(): Promise<PermissionState | null> {
  return (await queryMicPermissionStatus())?.state ?? null;
}

/**
 * Calls `onGranted` once, when the microphone permission changes to `granted`.
 * Never prompts. A no-op where the Permissions API can't answer for the
 * microphone. Returns a function that stops watching.
 */
export function watchMicPermissionGrant(onGranted: () => void): () => void {
  let stopped = false;
  let status: PermissionStatus | null = null;

  const onChange = () => {
    if (status?.state !== 'granted') return;
    stop();
    onGranted();
  };

  const stop = () => {
    stopped = true;
    status?.removeEventListener('change', onChange);
  };

  void queryMicPermissionStatus().then((result) => {
    if (stopped || !result) return;
    status = result;
    status.addEventListener('change', onChange);
  });

  return stop;
}
